import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Tab, Nav, Card, Button, Badge, Spinner, Accordion } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

const GATEAdvancedMathematicsPage = () => {
  const [numericalMethodsData, setNumericalMethodsData] = useState([]);
  const [aptitudeData, setAptitudeData] = useState([]);
  const [subjectSpecificData, setSubjectSpecificData] = useState({});
  const [resourcesData, setResourcesData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Simulate fetching data from database
  useEffect(() => {
    // In a real application, these would be API calls
    const fetchData = () => {
      setTimeout(() => {
        setNumericalMethodsData([
          { 
            id: 1, 
            title: 'Error Analysis', 
            topics: ['Round-off Errors', 'Truncation Errors', 'Error Propagation', 'Absolute and Relative Errors', 'Stability and Condition Numbers'],
            difficulty: 'Medium'
          },
          { 
            id: 2, 
            title: 'Solution of Equations', 
            topics: ['Bisection Method', 'Newton-Raphson Method', 'Secant Method', 'Fixed Point Iteration', 'Rate of Convergence', 'Multiple Roots'],
            difficulty: 'High'
          },
          { 
            id: 3, 
            title: 'Interpolation and Approximation', 
            topics: ['Lagrange Interpolation', 'Newton\'s Divided Difference', 'Spline Interpolation', 'Least Squares Approximation', 'Chebyshev Polynomials'],
            difficulty: 'High'
          },
          { 
            id: 4, 
            title: 'Numerical Differentiation & Integration', 
            topics: ['Forward/Backward/Central Differences', 'Trapezoidal Rule', 'Simpson\'s Rules', 'Gaussian Quadrature', 'Richardson Extrapolation'],
            difficulty: 'Medium'
          },
          { 
            id: 5, 
            title: 'Differential Equations', 
            topics: ['Euler\'s Method', 'Runge-Kutta Methods', 'Multistep Methods', 'Boundary Value Problems', 'Finite Difference Methods', 'Stability Analysis'],
            difficulty: 'Very High'
          },
          { 
            id: 6, 
            title: 'Linear Algebra Computation', 
            topics: ['Gaussian Elimination', 'LU Decomposition', 'Iterative Methods', 'Eigenvalue Problems', 'QR Algorithm', 'Singular Value Decomposition'],
            difficulty: 'High'
          },
        ]);
        
        setAptitudeData([
          { 
            id: 1, 
            title: 'Verbal Ability', 
            topics: ['English Grammar', 'Vocabulary', 'Reading Comprehension', 'Verbal Reasoning', 'Critical Reasoning'],
            importance: 'High'
          },
          { 
            id: 2, 
            title: 'Numerical Ability', 
            topics: ['Number Systems', 'Arithmetic', 'Algebra', 'Geometry and Mensuration', 'Probability', 'Statistics'],
            importance: 'Very High'
          },
          { 
            id: 3, 
            title: 'Analytical Ability', 
            topics: ['Logic', 'Data Interpretation', 'Spatial Ability', 'Data Sufficiency', 'Visual Reasoning'],
            importance: 'High'
          },
          { 
            id: 4, 
            title: 'General Knowledge', 
            topics: ['Current Affairs', 'Scientific Phenomena', 'Important Facts and Figures', 'Indian Context and Global Developments'],
            importance: 'Medium'
          },
        ]);
        
        setSubjectSpecificData({
          geology: [
            { id: 1, title: 'Mathematical Applications in Geology', topics: ['Statistical Analysis of Geological Data', 'Time Series Analysis', 'GIS and Remote Sensing Calculations', 'Resource Estimation Mathematics'] },
            { id: 2, title: 'Numerical Geology', topics: ['Numerical Stratigraphy', 'Geophysical Data Analysis', 'Seismic Wave Equations', 'Geostatistics and Kriging'] },
          ],
          chemistry: [
            { id: 1, title: 'Mathematical Chemistry', topics: ['Chemical Kinetics Calculations', 'Thermodynamic Equations', 'Quantum Chemistry', 'Computational Chemistry'] },
            { id: 2, title: 'Statistical Applications', topics: ['Error Analysis in Chemical Measurements', 'Design of Experiments', 'Statistical Thermodynamics', 'Chemometrics'] },
          ],
          biology: [
            { id: 1, title: 'Biostatistics', topics: ['Experimental Design', 'Hypothesis Testing', 'Multivariate Analysis', 'Biomedical Data Analysis'] },
            { id: 2, title: 'Mathematical Biology', topics: ['Population Dynamics', 'Epidemiological Models', 'Biological Networks', 'Bioinformatics Algorithms'] },
          ],
          physics: [
            { id: 1, title: 'Computational Physics', topics: ['Numerical Solutions to Physics Problems', 'Monte Carlo Methods', 'Molecular Dynamics', 'Finite Element Analysis'] },
            { id: 2, title: 'Mathematical Methods', topics: ['Vector Calculus', 'Complex Analysis', 'Tensor Analysis', 'Group Theory in Physics'] },
          ],
          engineering: [
            { id: 1, title: 'Engineering Mathematics', topics: ['Transform Theory', 'Partial Differential Equations', 'Optimization Methods', 'Numerical Methods for Engineers'] },
            { id: 2, title: 'Applied Statistics', topics: ['Quality Control', 'Reliability Analysis', 'Design of Experiments', 'Regression Analysis'] },
          ],
        });
        
        setResourcesData([
          { id: 1, title: 'Numerical Analysis', author: 'Richard L. Burden & J. Douglas Faires', type: 'Book', coverImg: '/api/placeholder/200/300', rating: 4.8 },
          { id: 2, title: 'Numerical Methods for Engineers', author: 'Steven C. Chapra & Raymond P. Canale', type: 'Book', coverImg: '/api/placeholder/200/300', rating: 4.7 },
          { id: 3, title: 'NPTEL Numerical Methods & Analysis', author: 'Dr. P. B. Sunil Kumar', type: 'Video Course', coverImg: '/api/placeholder/200/300', rating: 4.9 },
          { id: 4, title: 'Advanced Engineering Mathematics', author: 'Erwin Kreyszig', type: 'Book', coverImg: '/api/placeholder/200/300', rating: 4.6 },
          { id: 5, title: 'A Modern Approach to Verbal & Non-Verbal Reasoning', author: 'R.S. Aggarwal', type: 'Book', coverImg: '/api/placeholder/200/300', rating: 4.5 },
        ]);
        
        setLoading(false);
      }, 1000);
    };
    
    fetchData();
  }, []);

  const practiceTests = [
    {
      id: 1,
      title: "Weekly Numerical Methods Test",
      description: "Timed practice tests covering numerical methods and computational mathematics with GATE-level difficulty.",
      frequency: "Weekly",
      duration: "1 hour",
      questionCount: "20 questions",
      features: ["Detailed solutions", "Performance analytics", "Topic-wise strength analysis"]
    },
    {
      id: 2,
      title: "Subject-Specific Mathematics Module",
      description: "Tests focusing on mathematical applications in your specific GATE subject (Geology, Chemistry, Biology, etc.).",
      frequency: "Bi-weekly",
      duration: "90 minutes",
      questionCount: "30 questions",
      features: ["Subject integration", "Real GATE questions from past years", "Video explanations"]
    },
    {
      id: 3,
      title: "Aptitude Grand Test Series",
      description: "Comprehensive tests covering all aspects of GATE aptitude section with gradually increasing difficulty.",
      frequency: "Monthly",
      duration: "2 hours",
      questionCount: "50 questions",
      features: ["Time management strategies", "Common mistake analysis", "Personalized improvement plan"]
    }
  ];

  return (
    <div className="gate-adv-mathematics-page">
      <section className="page-header bg-black text-white py-5 my-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} className="text-center">
              <h1 className="display-5 fw-bold mb-3">Advanced Mathematics & Aptitude for GATE</h1>
              <p className="lead mb-4">Master numerical methods and aptitude across multiple subjects with our specialized GATE preparation program</p>
              <div className="d-flex justify-content-center gap-3">
                <Button variant="light" size="lg" href='/enroll' >Enroll Now</Button>
                <Button variant="outline-light" size="lg" href='/admission' >Free Trial</Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      
      {/* Main Content Area */}
      <section className="content-section py-5 bg-light">
        <Container>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading course materials...</p>
            </div>
          ) : (
            <Tab.Container id="main-tabs" defaultActiveKey="numerical">
              <Nav variant="pills" className="nav-tabs-custom justify-content-center mb-4">
                <Nav.Item>
                  <Nav.Link eventKey="numerical">Numerical Methods</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="aptitude">Aptitude Section</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="subject-specific">Subject Specific</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="resources">Study Resources</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="practice">Practice Tests</Nav.Link>
                </Nav.Item>
              </Nav>
              
              <Tab.Content>
                {/* Numerical Methods Tab */}
                <Tab.Pane eventKey="numerical">
                  <div className="mb-4">
                    <h2 className="h3 mb-3">Advanced Numerical Methods</h2>
                    <p className="text-muted">Master computational techniques essential for solving complex mathematical problems in GATE</p>
                  </div>
                  
                  <div className="numerical-methods-container">
                    <Row>
                      {numericalMethodsData.map(method => (
                        <Col md={6} lg={4} key={method.id} className="mb-4">
                          <Card className="h-100 shadow-sm">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                              <span className="fw-bold">{method.title}</span>
                              <Badge bg={
                                method.difficulty === 'Very High' ? 'danger' : 
                                method.difficulty === 'High' ? 'warning' : 
                                method.difficulty === 'Medium' ? 'info' : 'success'
                              }>
                                {method.difficulty}
                              </Badge>
                            </Card.Header>
                            <Card.Body>
                              <ul className="topic-list">
                                {method.topics.map((topic, index) => (
                                  <li key={index}>{topic}</li>
                                ))}
                              </ul>
                            </Card.Body>
                            <Card.Footer className="bg-white">
                              <div className="d-grid">
                                <Button variant="outline-primary" size="sm">Practice Problems</Button>
                              </div>
                            </Card.Footer>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Tab.Pane>
                
                {/* Aptitude Section Tab */}
                <Tab.Pane eventKey="aptitude">
                  <div className="mb-4">
                    <h2 className="h3 mb-3">GATE Aptitude Section</h2>
                    <p className="text-muted">Comprehensive preparation for the General Aptitude section common to all GATE papers</p>
                  </div>
                  
                  <div className="aptitude-container">
                    <Row>
                      {aptitudeData.map(section => (
                        <Col md={6} key={section.id} className="mb-4">
                          <Card className="h-100 shadow-sm">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                              <span className="fw-bold">{section.title}</span>
                              <Badge bg={
                                section.importance === 'Very High' ? 'danger' : 
                                section.importance === 'High' ? 'warning' : 
                                section.importance === 'Medium' ? 'info' : 'success'
                              }>
                                {section.importance} Priority
                              </Badge>
                            </Card.Header>
                            <Card.Body>
                              <ul className="topic-list">
                                {section.topics.map((topic, index) => (
                                  <li key={index}>{topic}</li>
                                ))}
                              </ul>
                            </Card.Body>
                            <Card.Footer className="bg-white">
                              <div className="d-flex gap-2">
                                <Button variant="outline-primary" size="sm" className="flex-grow-1">Practice Tests</Button>
                                <Button variant="outline-secondary" size="sm" className="flex-grow-1">Study Material</Button>
                              </div>
                            </Card.Footer>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                    
                    <div className="aptitude-tips mt-4 p-4 bg-white rounded shadow-sm">
                      <h3 className="h4 mb-3">Aptitude Preparation Tips</h3>
                      <Row>
                        <Col md={6}>
                          <ul className="check-list">
                            <li>Practice at least 10-15 problems daily from different topics</li>
                            <li>Focus on speed and accuracy with timed practice sessions</li>
                            <li>Learn shortcut techniques for calculation and reasoning</li>
                            <li>Review mistakes and understand the correct approach</li>
                          </ul>
                        </Col>
                        <Col md={6}>
                          <ul className="check-list">
                            <li>Create formula sheets for quick reference and revision</li>
                            <li>Take regular mock tests under exam conditions</li>
                            <li>Practice data interpretation with graphs and tables</li>
                            <li>Improve vocabulary through reading and language exercises</li>
                          </ul>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Tab.Pane>
                
                {/* Subject Specific Tab */}
                <Tab.Pane eventKey="subject-specific">
                  <div className="mb-4">
                    <h2 className="h3 mb-3">Subject-Specific Mathematics</h2>
                    <p className="text-muted">Specialized mathematical applications for different GATE subjects</p>
                  </div>
                  
                  <div className="subject-specific-container">
                    <Accordion defaultActiveKey="0" className="subject-accordion">
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>
                          <div className="d-flex align-items-center">
                            <span className="subject-icon">🪨</span>
                            <span className="fw-bold ms-2">Geology Mathematics</span>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="g-4">
                            {subjectSpecificData.geology.map(item => (
                              <Col md={6} key={item.id}>
                                <Card className="h-100 border-0 shadow-sm">
                                  <Card.Body>
                                    <Card.Title>{item.title}</Card.Title>
                                    <ul className="topic-list">
                                      {item.topics.map((topic, index) => (
                                        <li key={index}>{topic}</li>
                                      ))}
                                    </ul>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>
                      
                      <Accordion.Item eventKey="1">
                        <Accordion.Header>
                          <div className="d-flex align-items-center">
                            <span className="subject-icon">🧪</span>
                            <span className="fw-bold ms-2">Chemistry Mathematics</span>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="g-4">
                            {subjectSpecificData.chemistry.map(item => (
                              <Col md={6} key={item.id}>
                                <Card className="h-100 border-0 shadow-sm">
                                  <Card.Body>
                                    <Card.Title>{item.title}</Card.Title>
                                    <ul className="topic-list">
                                      {item.topics.map((topic, index) => (
                                        <li key={index}>{topic}</li>
                                      ))}
                                    </ul>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>
                      
                      <Accordion.Item eventKey="2">
                        <Accordion.Header>
                          <div className="d-flex align-items-center">
                            <span className="subject-icon">🧬</span>
                            <span className="fw-bold ms-2">Biology Mathematics</span>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="g-4">
                            {subjectSpecificData.biology.map(item => (
                              <Col md={6} key={item.id}>
                                <Card className="h-100 border-0 shadow-sm">
                                  <Card.Body>
                                    <Card.Title>{item.title}</Card.Title>
                                    <ul className="topic-list">
                                      {item.topics.map((topic, index) => (
                                        <li key={index}>{topic}</li>
                                      ))}
                                    </ul>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>
                      
                      <Accordion.Item eventKey="3">
                        <Accordion.Header>
                          <div className="d-flex align-items-center">
                            <span className="subject-icon">⚛️</span>
                            <span className="fw-bold ms-2">Physics Mathematics</span>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="g-4">
                            {subjectSpecificData.physics.map(item => (
                              <Col md={6} key={item.id}>
                                <Card className="h-100 border-0 shadow-sm">
                                  <Card.Body>
                                    <Card.Title>{item.title}</Card.Title>
                                    <ul className="topic-list">
                                      {item.topics.map((topic, index) => (
                                        <li key={index}>{topic}</li>
                                      ))}
                                    </ul>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>
                      
                      <Accordion.Item eventKey="4">
                        <Accordion.Header>
                          <div className="d-flex align-items-center">
                            <span className="subject-icon">⚙️</span>
                            <span className="fw-bold ms-2">Engineering Mathematics</span>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="g-4">
                            {subjectSpecificData.engineering.map(item => (
                              <Col md={6} key={item.id}>
                                <Card className="h-100 border-0 shadow-sm">
                                  <Card.Body>
                                    <Card.Title>{item.title}</Card.Title>
                                    <ul className="topic-list">
                                      {item.topics.map((topic, index) => (
                                        <li key={index}>{topic}</li>
                                      ))}
                                    </ul>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </div>
                </Tab.Pane>
                
                {/* Study Resources Tab */}
                <Tab.Pane eventKey="resources">
                  <div className="mb-4">
                    <h2 className="h3 mb-3">Recommended Study Resources</h2>
                    <p className="text-muted">Curated learning materials for advanced mathematics and aptitude</p>
                  </div>
                  
                  <div className="resources-container">
                    <Row className="g-4">
                      {resourcesData.map(resource => (
                        <Col md={6} lg={4} key={resource.id}>
                          <Card className="h-100 resource-card shadow-sm">
                            <Row className="g-0">
                              <Col xs={4}>
                                <img src={resource.coverImg} alt={resource.title} className="img-fluid rounded-start h-100 object-fit-cover" />
                              </Col>
                              <Col xs={8}>
                                <Card.Body>
                                  <Card.Title className="h5">{resource.title}</Card.Title>
                                  <Card.Text className="text-muted mb-1">{resource.author}</Card.Text>
                                  <Badge bg={resource.type === 'Book' ? 'primary' : 'success'} className="mb-2">{resource.type}</Badge>
                                  <div className="d-flex align-items-center mt-2">
                                    <span className="me-2">Rating:</span>
                                    <Badge bg="warning" text="dark">{resource.rating}/5.0</Badge>
                                  </div>
                                </Card.Body>
                              </Col>
                            </Row>
                            <Card.Footer className="bg-white border-0">
                              <Button variant="outline-primary" size="sm" className="w-100">View Details</Button>
                            </Card.Footer>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                    
                    <div className="additional-resources mt-4 p-4 bg-white rounded shadow-sm">
                      <h3 className="h4 mb-3">Additional Learning Resources</h3>
                      <Row>
                        <Col md={4} className="mb-3">
                          <div className="resource-category">
                            <h4 className="h5 mb-2">Online Courses</h4>
                            <ul className="resource-list">
                              <li>NPTEL Mathematics for GATE</li>
                              <li>Coursera Algorithm Specialization</li>
                              <li>edX Numerical Analysis Course</li>
                              <li>Udemy GATE Aptitude Masterclass</li>
                            </ul>
                          </div>
                        </Col>
                        <Col md={4} className="mb-3">
                          <div className="resource-category">
                            <h4 className="h5 mb-2">Practice Platforms</h4>
                            <ul className="resource-list">
                              <li>GATE Overflow Question Bank</li>
                              <li>GeeksforGeeks Numerical Methods</li>
                              <li>Made Easy Test Series</li>
                              <li>ACE Academy Mock Tests</li>
                            </ul>
                          </div>
                        </Col>
                        <Col md={4} className="mb-3">
                          <div className="resource-category">
                            <h4 className="h5 mb-2">Free Resources</h4>
                            <ul className="resource-list">
                              <li>MIT OpenCourseWare</li>
                              <li>Previous Year GATE Solutions</li>
                              <li>CSIR-NET Math Materials</li>
                              <li>Khan Academy Tutorials</li>
                            </ul>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Tab.Pane>
                
                {/* Practice Tests Tab */}
                <Tab.Pane eventKey="practice">
                  <div className="mb-4">
                    <h2 className="h3 mb-3">Practice Test Series</h2>
                    <p className="text-muted">Strengthen your preparation with our specialized test series</p>
                  </div>
                  
                  <div className="practice-tests-container">
                    <Row className="g-4">
                      {practiceTests.map(test => (
                        <Col md={6} lg={4} key={test.id}>
                          <Card className="h-100 test-card shadow">
                            <Card.Body>
                              <Card.Title className="h4 mb-3">{test.title}</Card.Title>
                              <Card.Text>{test.description}</Card.Text>
                              
                              <div className="test-details mt-4">
                                <div className="d-flex justify-content-between mb-2">
                                  <div className="detail-label">Frequency:</div>
                                  <div className="detail-value fw-bold">{test.frequency}</div>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                  <div className="detail-label">Duration:</div>
                                  <div className="detail-value fw-bold">{test.duration}</div>
                                </div>
                                <div className="d-flex justify-content-between mb-3">
                                  <div className="detail-label">Questions:</div>
                                  <div className="detail-value fw-bold">{test.questionCount}</div>
                                </div>
                              </div>
                              
                              <div className="test-features mt-3">
                                <div className="feature-label mb-2">Features:</div>
                                <ul className="feature-list ps-3">
                                  {test.features.map((feature, idx) => (
                                    <li key={idx}>{feature}</li>
                                  ))}
                                </ul>
                              </div>
                            </Card.Body>
                            <Card.Footer className="bg-white border-0">
                              <Button variant="primary" className="w-100">Enroll in Test Series</Button>
                            </Card.Footer>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                    
                    <div className="test-statistics mt-5">
                      <h3 className="h4 mb-4 text-center">Test Series Performance Statistics</h3>
                      <Row className="g-4 text-center">
                        <Col md={3}>
                          <div className="stat-card bg-white p-4 rounded shadow-sm">
                            <div className="stat-value display-6 fw-bold text-primary mb-2">93%</div>
                            <div className="stat-label">Students improved scores</div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="stat-card bg-white p-4 rounded shadow-sm">
                            <div className="stat-value display-6 fw-bold text-primary mb-2">82%</div>
                            <div className="stat-label">Time management improvement</div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="stat-card bg-white p-4 rounded shadow-sm">
                            <div className="stat-value display-6 fw-bold text-primary mb-2">15+</div>
                            <div className="stat-label">Avg. score increase</div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="stat-card bg-white p-4 rounded shadow-sm">
                            <div className="stat-value display-6 fw-bold text-primary mb-2">750+</div>
                            <div className="stat-label">Practice questions</div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          )}
        </Container>
      </section>
      
      {/* Call to Action Section */}
      <section className="cta-section py-5 bg-gradient-secondary text-white">
        <Container>
          <Row className="justify-content-between align-items-center">
            <Col lg={7} className="mb-4 mb-lg-0">
              <h2 className="h3 fw-bold mb-3">Ready to Master Advanced Mathematics for GATE?</h2>
              <p className="lead mb-0">Join our specialized program and get access to comprehensive study materials, practice tests, and expert guidance.</p>
            </Col>
            <Col lg={4}>
              <div className="cta-buttons d-flex flex-column gap-2">
                <Button variant="light" size="lg" className="mb-2">Enroll Now with 20% Discount</Button>
                <Button variant="outline-light">Schedule a Free Counseling Session</Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default GATEAdvancedMathematicsPage;

// Add CSS for styling
export const additionalCSS = `
.bg-gradient-primary {
  background: linear-gradient(135deg, #0d6efd, #1266f1, #0a47bc);
}

.bg-gradient-secondary {
  background: linear-gradient(135deg, #6c757d, #495057, #343a40);
}

.nav-tabs-custom {
  border-radius: 50px;
  overflow: hidden;
  display: inline-flex;
  padding: 5px;
  background-color:rgb(1, 53, 105);
  border: 1px solid #dee2e6;
}

.nav-tabs-custom .nav-link {
  border-radius: 50px;
  padding: 10px 20px;
  transition: all 0.3s ease;
  margin: 0 3px;
  font-weight: 500;
}

.nav-tabs-custom .nav-link.active {
  background-color: #0d6efd;
  color: white;
  box-shadow: 0 4px 6px rgba(13, 110, 253, 0.25);
}

.topic-list {
  padding-left: 1.2rem;
  margin-bottom: 0;
}

.topic-list li {
  margin-bottom: 0.5rem;
}

.topic-list li:last-child {
  margin-bottom: 0;
}

.check-list {
  list-style: none;
  padding-left: 0;
}

.check-list li {
  position: relative;
  padding-left: 1.8rem;
  margin-bottom: 0.7rem;
}

.check-list li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #0d6efd;
  font-weight: bold;
}

.resource-list {
  list-style: none;
  padding-left: 0;
}

.resource-list li {
  padding: 8px 0;
  border-bottom: 1px dashed #dee2e6;
}

.resource-list li:last-child {
  border-bottom: none;
}

.subject-icon {
  font-size: 1.5rem;
  display: inline-block;
  width: 32px;
  height: 32px;
  text-align: center;
  line-height: 32px;
}

.subject-accordion .accordion-button:not(.collapsed) {
  background-color: #f8f9fa;
  color: #0d6efd;
  box-shadow: none;
}

.subject-accordion .accordion-button:focus {
  box-shadow: none;
  border-color: rgba(13, 110, 253, 0.25);
}

.test-card {
  transition: transform 0.3s ease;
}

.test-card:hover {
  transform: translateY(-5px);
}

.detail-label {
  color: #6c757d;
}

.feature-list {
  margin-bottom: 0;
}

.stat-card {
  height: 100%;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
}

/* Responsive adjustments */
@media (max-width: 767.98px) {
  .nav-tabs-custom {
    display: flex;
    flex-wrap: wrap;
  }
  
  .nav-tabs-custom .nav-link {
    padding: 8px 15px;
    margin-bottom: 5px;
  }
  
  .stat-card {
    margin-bottom: 1rem;
  }
}

/* Additional animations */
.page-header, .cta-section {
  position: relative;
  overflow: hidden;
}

.page-header:before, .cta-section:before {
  content: '';
  position: absolute;
  width: 150%;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  transform: rotate(-45deg);
  top: -50%;
  left: -50%;
  animation: shine 3s infinite linear;
}

@keyframes shine {
  0% {
    left: -100%;
    top: -100%;
  }
  100% {
    left: 50%;
    top: 50%;
  }
}

/* Print styles */
@media print {
  .page-header, .cta-section {
    background: #f8f9fa !important;
    color: #212529 !important;
  }
  
  .nav-tabs-custom, .btn {
    display: none !important;
  }
  
  .tab-content > .tab-pane {
    display: block !important;
    opacity: 1 !important;
    visibility: visible !important;
  }
}`;